import { createTheme } from '@mui/material/styles';

/**
 * FamilyForLife - Tree Theme (Nature-Inspired)
 *
 * Design Philosophy:
 * - Green and brown colors representing family trees and nature
 * - Warm, earthy tones creating a grounded, nurturing atmosphere
 * - Natural growth and connection symbolism
 * - Accessible and inviting for all ages
 */

// Shared typography settings
const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.6,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.75,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.66,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 2.66,
    textTransform: 'uppercase',
    letterSpacing: '0.08333em',
  },
};

// Shared shape settings
const shape = {
  borderRadius: 12,
};

// Shared component overrides (mode-independent)
const getComponents = (mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollBehavior: 'smooth',
      },
      '*::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '*::-webkit-scrollbar-track': {
        background: mode === 'dark' ? '#1a1a1a' : '#f1f1f1',
      },
      '*::-webkit-scrollbar-thumb': {
        background: mode === 'dark' ? '#4caf50' : '#8bc34a',
        borderRadius: '4px',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        background: mode === 'dark' ? '#66bb6a' : '#689f38',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '8px 20px',
        fontWeight: 600,
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
        },
      },
      sizeLarge: {
        padding: '12px 28px',
        fontSize: '1rem',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
        // Ensure minimum touch target height for clickable chips
        minHeight: 32,
      },
      clickable: {
        minHeight: 44,
      },
      deletable: {
        minHeight: 44,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 6,
        fontSize: '0.75rem',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.9375rem',
        minHeight: 48,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        // Ensure minimum 44x44px touch target for accessibility
        minWidth: 44,
        minHeight: 44,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.1)',
        },
      },
      // Override small size to still maintain touch target
      sizeSmall: {
        minWidth: 44,
        minHeight: 44,
        padding: 10, // Center the smaller icon within the touch target
      },
      sizeMedium: {
        minWidth: 44,
        minHeight: 44,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        marginBottom: 4,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        // Ensure minimum touch target height
        minHeight: 48,
        borderRadius: 8,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        // Ensure minimum touch target height for menu items
        minHeight: 44,
      },
    },
  },
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        // Ensure minimum touch target for bottom nav
        minHeight: 56,
        minWidth: 80,
      },
    },
  },
  MuiSnackbar: {
    defaultProps: {
      autoHideDuration: 4000,
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    },
    styleOverrides: {
      // Bottom-positioned snackbars need extra spacing for bottom navigation
      anchorOriginBottomCenter: {
        bottom: 72, // 56px bottom nav + 16px margin
        // On desktop (600px+), position at bottom-right instead
        '@media (min-width: 600px)': {
          left: 'auto',
          right: 24,
          transform: 'none',
        },
      },
      anchorOriginBottomRight: {
        bottom: 72, // Consistent spacing if component explicitly uses bottom-right
      },
      anchorOriginBottomLeft: {
        bottom: 72, // Consistent spacing if component explicitly uses bottom-left
      },
    },
  },
});

// Light Theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#6d4c41',
      light: '#8d6e63',
      dark: '#5d4037',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#689f38',
      light: '#8bc34a',
      dark: '#558b2f',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#c62828',
      light: '#e53935',
      dark: '#b71c1c',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(33, 33, 33, 0.87)',
      secondary: 'rgba(66, 66, 66, 0.7)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(46, 125, 50, 0.12)',
  },
  typography: {
    ...typography,
    h1: { ...typography.h1, color: '#1b5e20' },
    h2: { ...typography.h2, color: '#2e7d32' },
    h3: { ...typography.h3, color: '#2e7d32' },
    h4: { ...typography.h4, color: '#2e7d32' },
    h5: { ...typography.h5, color: '#2e7d32' },
    h6: { ...typography.h6, color: '#2e7d32' },
  },
  shape,
  components: getComponents('light'),
});

// Dark Theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#000000',
    },
    secondary: {
      main: '#a1887f',
      light: '#bcaaa4',
      dark: '#8d6e63',
      contrastText: '#000000',
    },
    success: {
      main: '#81c784',
      light: '#a5d6a7',
      dark: '#66bb6a',
      contrastText: '#000000',
    },
    warning: {
      main: '#ffb74d',
      light: '#ffd54f',
      dark: '#ffa726',
      contrastText: '#000000',
    },
    error: {
      main: '#ef5350',
      light: '#e57373',
      dark: '#f44336',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#4fc3f7',
      light: '#81d4fa',
      dark: '#29b6f6',
      contrastText: '#000000',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.87)',
      secondary: 'rgba(255, 255, 255, 0.6)',
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    divider: 'rgba(76, 175, 80, 0.2)',
  },
  typography: {
    ...typography,
    h1: { ...typography.h1, color: '#81c784' },
    h2: { ...typography.h2, color: '#66bb6a' },
    h3: { ...typography.h3, color: '#66bb6a' },
    h4: { ...typography.h4, color: '#66bb6a' },
    h5: { ...typography.h5, color: '#66bb6a' },
    h6: { ...typography.h6, color: '#66bb6a' },
  },
  shape,
  components: getComponents('dark'),
});

// Default export for backwards compatibility
export default lightTheme;
